import {
  EntryType,
  getTimelineWeekRange,
  Octopus,
} from '@maximizer/core/shared/domain';
import { formatISO } from 'date-fns';
import { TimelineFilter } from '../state/timeline.models';

export abstract class BaseTimelineService {
  protected getFilterQuery({
    interactions,
    notes,
    categories,
  }: TimelineFilter): Octopus.LogicalQuery<Octopus.Timeline> {
    const query: Octopus.LogicalQuery<Octopus.Timeline> = {
      $OR: [],
    };

    if (categories?.length) {
      const includeDocuments =
        interactions.length === 0 ||
        interactions.includes(Octopus.DefaultInteractions.DOCUMENT);
      const includeAllNotes =
        (interactions.length === 0 && notes.length === 0) ||
        interactions.includes(Octopus.DefaultInteractions.NOTE);
      const categoryFilter: Octopus.LogicalQuery<Octopus.Timeline> = {
        $AND: [
          {
            Category: {
              $IN: categories,
            },
          },
        ],
      };

      if (interactions?.length) {
        categoryFilter.$AND?.push({
          Type: {
            $IN: interactions,
          },
        });
      }

      query.$OR?.push(categoryFilter);

      if (includeDocuments) {
        query.$OR?.push({
          Type: {
            $EQ: Octopus.DefaultInteractions.DOCUMENT,
          },
        });
      }

      if (includeAllNotes) {
        query.$OR?.push({
          Type: {
            $EQ: Octopus.DefaultInteractions.NOTE,
          },
        });
      }
    } else if (interactions?.length) {
      query.$OR?.push({
        Type: {
          $IN: interactions,
        },
      });
    }

    if (notes?.length) {
      query.$OR?.push({
        NoteType: {
          $IN: notes,
        },
      });
    }

    return query;
  }

  protected getCriteria(
    filter: TimelineFilter,
    key?: string | null,
    type?: EntryType,
    ignoreFilters = false,
    ignoreKey = false,
  ): Octopus.Criteria<Octopus.Timeline> {
    const weeks = getTimelineWeekRange(filter.referenceDate);
    const filterQuery = !ignoreFilters ? this.getFilterQuery(filter) : {};

    const query: Octopus.LogicalQuery<Octopus.Timeline> = {
      $AND: [],
    };

    this.setQueryKey(query, filter, key, type, ignoreKey);

    query.$AND?.push(filterQuery);

    if (filter.users?.length) {
      query.$AND?.push({
        'Users/Key': {
          $IN: filter.users.map((user) => {
            return { Uid: user };
          }),
        },
      });
    }

    if (filter.showCompleted !== undefined) {
      query.$AND?.push({
        Completed: {
          $EQ: filter.showCompleted,
        },
      });
    }

    if (!filter.customDateQuery) {
      query.$AND?.push({
        StartDate: {
          $RANGE: [
            formatISO(weeks.last.start, { representation: 'date' }),
            formatISO(weeks.next.end, { representation: 'date' }),
          ],
        },
      });
    } else {
      query.$AND?.push({
        StartDate: filter.customDateQuery,
      });
    }

    if (filter.searchPhrase?.length) {
      query.$AND?.push({
        $PHRASE: filter.searchPhrase,
      });
    }

    return {
      SearchQuery: query,
    };
  }

  protected setQueryKey(
    query: Octopus.LogicalQuery<Octopus.Timeline>,
    filter: TimelineFilter,
    key?: string | null,
    type?: EntryType,
    ignoreKey = false,
  ): void {
    if (type && (key || ignoreKey)) {
      switch (type) {
        case 'addressBook':
          if (filter.showAll) {
            query.$AND?.push({
              'AbEntries/Key': {
                $EQ: {
                  Body: key,
                  OrRelated: true,
                },
              },
            });
          } else {
            query.$AND?.push({
              'AbEntries/Key': {
                $EQ: key,
              },
            });
          }
          break;

        case 'lead':
          query.$AND?.push({
            'Leads/$item/Key': {
              $EQ: key,
            },
          });
          break;

        case 'case':
        case 'opportunity':
          query.$AND?.push({
            WithKey: {
              $EQ: key,
            },
          });
          break;

        default:
          break;
      }
    }
  }
}
